/**
 * Admin Shopify Manager Component
 * 
 * Handles Shopify connection, authentication, and product import
 */

import { useState, useEffect } from 'react'
import { simpleShopifyService } from '../utils/shopifyServiceSimple'

function AdminShopifyManager() {
  const [connectionStatus, setConnectionStatus] = useState<any>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const [products, setProducts] = useState<any[]>([])
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const itemsPerPage = 12

  useEffect(() => {
    // Simple service doesn't need authentication - just check status
    updateConnectionStatus()
    
    // Auto-load products when component mounts
    if (simpleShopifyService.isConfigured()) {
      handleLoadProducts()
    }
  }, [])

  const updateConnectionStatus = () => {
    const status = simpleShopifyService.getStatus()
    setConnectionStatus(status)
    console.log('Shopify connection status:', status)
  }

  const handleTestConnection = async () => {
    setIsTestingConnection(true)
    try {
      const result = await simpleShopifyService.testConnection()
      alert(result.message)
    } catch (error) {
      alert('Connection test failed: ' + (error as Error).message)
    }
    setIsTestingConnection(false)
  }

  const handleLoadProducts = async () => {
    setIsLoadingProducts(true)
    try {
      console.log('AdminShopifyManager: Loading all products...')
      const shopifyProducts = await simpleShopifyService.getProducts(3000) // Load all products (you have ~2,780)
      setProducts(shopifyProducts)
      console.log('AdminShopifyManager: Loaded', shopifyProducts.length, 'products')
    } catch (error) {
      console.error('Error loading products:', error)
      alert('Error loading products: ' + (error as Error).message)
    }
    setIsLoadingProducts(false)
  }

  // Pagination logic
  const totalPages = Math.ceil(products.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentProducts = products.slice(startIndex, endIndex)

  if (!connectionStatus) {
    return <div className="text-center py-4">Loading Shopify configuration...</div>
  }

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="fs-2 fw-bold text-dark">Shopify Integration</h2>
        <div className="d-flex gap-2">
          <button 
            onClick={handleTestConnection}
            disabled={isTestingConnection || !connectionStatus.configured}
            className="btn btn-primary"
          >
            {isTestingConnection ? 'Testing...' : 'Test Connection'}
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Connection Status</h5>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Configuration:</span>
                <span className={`fw-bold ${connectionStatus.configured ? 'text-success' : 'text-danger'}`}>
                  {connectionStatus.configured ? '✅ Complete' : '❌ Missing'}
                </span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Store:</span>
                <span className="fw-bold">{connectionStatus.storeUrl || 'Not set'}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Credentials:</span>
                <span className={`fw-bold ${connectionStatus.hasCredentials ? 'text-success' : 'text-danger'}`}>
                  {connectionStatus.hasCredentials ? '✅ Present' : '❌ Missing'}
                </span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Connected:</span>
                <span className={`fw-bold ${connectionStatus.connected ? 'text-success' : 'text-warning'}`}>
                  {connectionStatus.connected ? '✅ Yes' : '⚠️ No'}
                </span>
              </div>
            </div>
          </div>

          {!connectionStatus.configured && (
            <div className="mt-3 alert alert-warning">
              <strong>Configuration Required:</strong> Please check your .env file for Shopify credentials.
            </div>
          )}

          {!connectionStatus.connected && connectionStatus.configured && (
            <div className="mt-3 alert alert-info">
              <strong>Authentication Required:</strong> Click "Connect to Shopify" to authorize access to your store.
            </div>
          )}
        </div>
      </div>

      {/* Product Catalog */}
      {connectionStatus.connected && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">Shopify Product Catalog</h5>
              <div className="d-flex gap-2">
                <button
                  onClick={handleLoadProducts}
                  disabled={isLoadingProducts}
                  className="btn btn-outline-primary btn-sm"
                >
                  {isLoadingProducts ? 'Refreshing...' : 'Refresh Products'}
                </button>
              </div>
            </div>

            {isLoadingProducts && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading products...</span>
                </div>
                <p className="mt-3 text-muted">Loading all products from Shopify...</p>
              </div>
            )}

            {!isLoadingProducts && products.length > 0 && (
              <>
                {/* Results Summary */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">
                    Showing {startIndex + 1}-{Math.min(endIndex, products.length)} of {products.length} products
                    {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                  </p>
                  <small className="text-muted">
                    All products automatically appear in public gallery
                  </small>
                </div>

                {/* Product Grid */}
                <div className="row g-3">
                  {currentProducts.map(product => (
                    <div key={product.id} className="col-md-6 col-lg-4">
                      <div className="card h-100">
                        <div className="card-img-top bg-light d-flex align-items-center justify-content-center" style={{height: '150px'}}>
                          {product.images[0] ? (
                            <img 
                              src={`${product.images[0].src}${product.images[0].src.includes('?') ? '&' : '?'}width=300`}
                              alt={product.title}
                              className="w-100 h-100"
                              style={{
                                objectFit: 'cover',
                                imageRendering: 'auto',
                                maxWidth: '100%'
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <div className="text-muted">📦</div>
                          )}
                        </div>
                        <div className="card-body d-flex flex-column">
                          <h6 className="card-title">{product.title}</h6>
                          <p className="card-text small text-muted flex-grow-1">
                            {product.vendor} • ${product.variants[0]?.price || '0.00'}
                          </p>
                          <div className="d-flex gap-2">
                            <a
                              href={simpleShopifyService.getProductUrl(product.handle)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              View in Store
                            </a>
                            <span className="badge bg-success align-self-center">Live in Gallery</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <nav aria-label="Products pagination" className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage > totalPages - 3) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}

            {!isLoadingProducts && products.length === 0 && (
              <div className="text-center py-4">
                <div className="text-muted">
                  <div className="display-4 mb-3">📦</div>
                  <h5>No Products Found</h5>
                  <p>Click "Refresh Products" to load your Shopify catalog.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminShopifyManager